import { useSyncExternalStore } from "react";
import type { Lang } from "./i18n";

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

// We restore scroll ourselves (saved per history entry) instead of letting
// the browser guess on SPA navigations.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/** Push a new URL (path + optional query) and re-render subscribers. */
export function navigate(to: string) {
  if (to === window.location.pathname + window.location.search) return;
  // Remember where we were so Back can restore the scroll position.
  window.history.replaceState({ scrollY: window.scrollY }, "");
  window.history.pushState({}, "", to);
  emit();
}

/** Update only the query string of the current entry, without a history push. */
export function replaceQuery(search: string) {
  const next = window.location.pathname + (search ? `?${search}` : "");
  window.history.replaceState({ ...window.history.state }, "", next);
}

function onPopState() {
  emit();
  // Restore the saved scroll after the view re-renders.
  const y = window.history.state?.scrollY;
  if (typeof y === "number") {
    requestAnimationFrame(() => window.scrollTo(0, y));
  }
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  window.addEventListener("popstate", onPopState);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("popstate", onPopState);
  };
}

/** Re-renders on any history navigation; returns the current location key. */
export function useLocation(): string {
  return useSyncExternalStore(
    subscribe,
    () => window.location.pathname + window.location.search,
    () => "/",
  );
}

export interface Route {
  lang: Lang;
  slug: string; // "" → list view
}

/** Parse the path into { lang, slug }. French is the `/fr` prefix. */
export function useRoute(): Route {
  const path = useLocation().split("?")[0];
  const segs = path.split("/").filter(Boolean).map(decodeURIComponent);
  if (segs[0] === "fr") return { lang: "fr", slug: segs[1] ?? "" };
  return { lang: "en", slug: segs[0] ?? "" };
}

/** Build a path for a given language: en → "/...", fr → "/fr/...". */
export function langPath(lang: Lang, slug = ""): string {
  const base = lang === "fr" ? "/fr" : "";
  return slug ? `${base}/${slug}` : base || "/";
}
